import { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import DoramaCard from '../components/DoramaCard';
import SkeletonCard from '../components/SkeletonCard';
import { useGetMaterialsQuery } from '../services/materialsApi';
import styles from '../css/Catalog.module.css';

const PER_PAGE = 24;
const MAX_ITEMS = 100;

const Top100 = () => {
  const [page, setPage] = useState(1);
  const [allDramas, setAllDramas] = useState([]);
  const sentinelRef = useRef(null);

  const { data, isFetching } = useGetMaterialsQuery({
    sort_by: 'imdb_rating',
    sort_order: 'desc',
    per_page: PER_PAGE,
    page,
  });

  useEffect(() => {
    if (data?.data) {
      setAllDramas(prev => {
        const existingIds = new Set(prev.map(d => d.id));
        const newItems = data.data.filter(d => !existingIds.has(d.id));
        return [...prev, ...newItems].slice(0, MAX_ITEMS);
      });
    }
  }, [data]);

  const totalPages = data?.meta?.total_pages ?? 1;
  const maxPages = Math.ceil(MAX_ITEMS / PER_PAGE);
  const hasMore = allDramas.length < MAX_ITEMS && page < Math.min(totalPages, maxPages);

  const handleObserver = useCallback((entries) => {
    const [entry] = entries;
    if (entry.isIntersecting && !isFetching && hasMore) {
      setPage(p => p + 1);
    }
  }, [isFetching, hasMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  return (
    <div className={styles.catalogPage}>
      <Helmet>
        <title>Топ 100 лучших дорам — DoramaHype</title>
        <meta name="description" content="Топ 100 лучших корейских дорам всех времён с рейтингами IMDb, KinoPoisk и MyDramaList. Смотрите онлайн на DoramaHype." />
        <meta property="og:title" content="Топ 100 лучших дорам — DoramaHype" />
        <meta property="og:url" content="https://doramahype.ru/top-100" />
      </Helmet>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Топ 100</h1>
          <p className={styles.subtitle}>Лучшие дорамы по рейтингу IMDB</p>
        </div>

        <div className={styles.dramasGrid}>
          {allDramas.length === 0
            ? Array.from({ length: 24 }, (_, i) => <SkeletonCard key={i} />)
            : allDramas.map(drama => (
                <DoramaCard
                  key={drama.id}
                  id={drama.id}
                  slug={drama.slug}
                  slug_id={drama.slug_id}
                  image={`https://doramahype.ru/uploads/posters/${drama.poster_url}`}
                  title={drama.title}
                  rating={drama.imdb_rating}
                  year={drama.year}
                  genres={drama.genres ?? []}
                  status={drama.drama_status}
                />
              ))
          }
        </div>

        <div ref={sentinelRef} className={styles.sentinel} />

        {isFetching && allDramas.length > 0 && (
          <div className={styles.loadingMore}>
            <div className={styles.spinner} />
          </div>
        )}

        {!hasMore && allDramas.length === MAX_ITEMS && (
          <div className={styles.endMessage}>Показано все 100 дорам</div>
        )}
      </div>
    </div>
  );
};

export default Top100;
